import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Coins, X, ArrowRight, PartyPopper } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { IncludedList } from './pro/IncludedList';

const fill = (t, vars) => String(t || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ''));

/** The popup body — reused by the admin Promotions preview. */
export function ProWelcomeContent({ data, onClose, onGo }) {
  const c = data.config;
  const vars = { name: data.name || 'there', credits: data.credits?.welcome ?? 25, balance: data.credits?.balance ?? 0, section_cost: data.credits?.section_cost ?? 25 };
  return (
    <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl shadow-2xl max-h-[92vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()} data-testid="pro-welcome-modal">
      <div className="relative bg-[#0B1120] text-white px-6 pt-7 pb-6 rounded-t-3xl overflow-hidden">
        <div className="absolute -top-20 -right-10 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl" />
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="pro-welcome-close"><X size={20} /></button>
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30"><Crown size={26} /></div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300 inline-flex items-center gap-1.5"><PartyPopper size={12} /> You're in</p>
            <h2 className="font-[Manrope] text-2xl sm:text-3xl font-extrabold leading-tight mt-1" data-testid="pro-welcome-title">{fill(c.welcome_title, vars)}</h2>
            <p className="text-sm text-white/70 mt-1.5" data-testid="pro-welcome-subtitle">{fill(c.welcome_subtitle, vars)}</p>
          </div>
        </div>
      </div>
      <div className="px-6 py-5 space-y-5">
        <IncludedList features={data.features || []} isPro compact />
        <div className="rounded-2xl border-2 border-teal-300 bg-gradient-to-r from-teal-50 to-emerald-50 p-4 flex items-start gap-3 relative overflow-hidden" data-testid="pro-welcome-credits">
          <span className="absolute top-0 right-4 text-[10px] font-bold uppercase tracking-wide bg-teal-700 text-white px-2 py-0.5 rounded-b-lg">Included</span>
          <div className="w-12 h-12 rounded-2xl bg-teal-700 text-white flex items-center justify-center shrink-0"><Coins size={22} /></div>
          <div className="flex-1 min-w-0">
            <p className="font-[Manrope] font-extrabold text-slate-900 text-base sm:text-lg" data-testid="pro-welcome-credits-title">{fill(c.welcome_credits_title, vars)}</p>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">{fill(c.welcome_credits_text, vars)}</p>
          </div>
          <div className="hidden sm:block text-center shrink-0 pl-2">
            <p className="font-[Manrope] text-4xl font-extrabold text-teal-800 leading-none" data-testid="pro-welcome-credits-number">{vars.credits}</p>
            <p className="text-[11px] font-bold text-teal-700 uppercase tracking-wide mt-1">credits</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-1">
          <button type="button" onClick={() => onGo(c.welcome_cta_url)} className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-6 py-3 shadow-lg shadow-blue-500/25" data-testid="pro-welcome-cta">{c.welcome_cta_label} <ArrowRight size={15} /></button>
          {c.welcome_secondary_label && <button type="button" onClick={() => onGo(c.welcome_secondary_url)} className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 text-slate-800 text-sm font-semibold px-6 py-3 hover:border-slate-400" data-testid="pro-welcome-secondary">{c.welcome_secondary_label}</button>}
          <button type="button" onClick={onClose} className="sm:ml-auto text-sm font-semibold text-slate-400 hover:text-slate-700 px-2 py-2" data-testid="pro-welcome-dismiss">{c.welcome_dismiss_label || 'Close'}</button>
        </div>
      </div>
    </div>
  );
}

/** Mounted in DashboardLayout: shows once per student right after they become Pro (server-side flag). */
export default function ProWelcomeModal() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const eligible = !!user && user.role === 'user' && user.subscription_status && user.subscription_status !== 'free' && !user.pro_welcome_seen;
  useEffect(() => {
    if (!eligible) return;
    api.get('/api/pro-welcome').then((r) => { if (r.data?.show) setData(r.data); }).catch(() => {});
  }, [eligible]);
  if (!data) return null;
  const close = () => {
    setData(null);
    api.post('/api/pro-welcome/seen').catch(() => {});
    const next = { ...user, pro_welcome_seen: true };
    setUser?.(next);
    try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* ignore */ }
  };
  const go = (url) => { close(); if (url) { if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer'); else navigate(url); } };
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close} data-testid="pro-welcome-backdrop">
      <ProWelcomeContent data={data} onClose={close} onGo={go} />
    </div>
  );
}
